import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui';
import { CheckCircle, Mail, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { applyActionCode, reload } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { AuthService } from '@/services/auth';
import { useAuthStore } from '@/store/auth';
import { academyService } from '@/services/academy';
import { publicOrgService } from '@/services/publicOrg';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { AcademyBrandingProvider } from '@/contexts/AcademyBrandingContext';
import type { Academy } from '@court-sync/shared';
import type { PublicOrganizationProfile } from '@/services/publicOrg';
import { buildOrgLoginPath } from '@/lib/orgPaths';
import {
  buildPostVerificationOrgLoginTarget,
  buildResendVerificationReturnUrl,
  isAuthenticatedPostVerificationRoute,
  parseReturnUrl,
  resolveEffectiveAcademySlug,
  resolveEmailVerificationAcademySlug,
  resolveUnsignedPostVerificationRedirect,
} from '@/utils/emailVerificationRedirect';

export default function OrgEmailVerificationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const orgSlug = useOrgSlug();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [orgProfile, setOrgProfile] = useState<PublicOrganizationProfile | null>(null);
  const [academy, setAcademy] = useState<Academy | null>(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const verificationAttemptedRef = useRef(false);

  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  const academySlugParam = searchParams.get('academySlug');

  const verificationEmailSendFailed = Boolean(
    (location.state as { verificationEmailSendFailed?: boolean } | null)?.verificationEmailSendFailed
  );

  const [returnUrl] = useState(
    () => searchParams.get('returnUrl') || (location.state as { returnUrl?: string } | null)?.returnUrl
  );

  const orgFields = orgProfile
    ? {
        slug: orgProfile.organization.slug,
        subdomain: orgProfile.organization.subdomain,
        subdomainEnabled: orgProfile.organization.subdomainEnabled,
      }
    : orgSlug
      ? { slug: orgSlug }
      : null;

  const { p, hostMode } = useOrgPaths(orgFields);

  useEffect(() => {
    if (!orgSlug) {
      setLoadingOrg(false);
      return;
    }
    publicOrgService
      .getProfile(orgSlug)
      .then(async (res) => {
        if (!res.success || !res.data) {
          setOrgProfile(null);
          return;
        }
        setOrgProfile(res.data);
        const slug =
          academySlugParam?.trim() ||
          res.data.academySlug ||
          resolveEmailVerificationAcademySlug({ returnUrl, currentOrigin: window.location.origin });
        if (slug) {
          try {
            const loaded = await academyService.getAcademyBySlug(slug);
            setAcademy(loaded);
          } catch {
            setAcademy(null);
          }
        }
      })
      .finally(() => setLoadingOrg(false));
  }, [academySlugParam, orgSlug, returnUrl]);

  useEffect(() => {
    if (searchParams.get('returnUrl')) {
      const params = new URLSearchParams(searchParams);
      params.delete('returnUrl');
      const cleanSearch = params.toString();
      navigate(`${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`, { replace: true });
    }
  }, [location.pathname, navigate, searchParams]);

  const resolvedAcademySlug = academy?.slug ?? academySlugParam ?? orgProfile?.academySlug ?? null;

  const getEffectiveAcademySlug = useCallback(
    () =>
      resolveEffectiveAcademySlug({
        resolvedAcademySlug,
        academySlugParam,
      }),
    [academySlugParam, resolvedAcademySlug]
  );

  const applyPostVerificationRedirect = useCallback(
    (redirect: { type: 'external'; href: string } | { type: 'navigate'; href: string; state?: { email?: string; message?: string } }) => {
      if (redirect.type === 'external') {
        window.location.assign(redirect.href);
        return;
      }
      navigate(redirect.href, { state: redirect.state });
    },
    [navigate]
  );

  const navigateToLogin = useCallback(
    (message: string) => {
      if (!orgFields) {
        navigate('/login', { state: { email: email ?? undefined, message } });
        return;
      }

      const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
      const defaultReturn = p?.('/dashboard') ?? `/club-academy/${orgFields.slug}/dashboard`;

      if (returnUrl) {
        const parsed = parseReturnUrl(returnUrl, currentOrigin);
        const unsignedRedirect = resolveUnsignedPostVerificationRedirect({
          parsed,
          effectiveAcademySlug: getEffectiveAcademySlug(),
          effectiveOrgSlug: orgFields.slug,
          org: orgFields,
          academy,
          email,
          loginMessage: message,
          orgHostMode: hostMode,
        });
        if (unsignedRedirect) {
          applyPostVerificationRedirect(unsignedRedirect);
          return;
        }
      }

      const loginTarget = buildPostVerificationOrgLoginTarget({
        org: orgFields,
        returnPath: returnUrl ? parseReturnUrl(returnUrl, currentOrigin).path : defaultReturn,
        email,
        message,
        hostMode,
      });
      navigate(loginTarget.href, { state: { email: email ?? undefined, message } });
    },
    [academy, applyPostVerificationRedirect, email, getEffectiveAcademySlug, hostMode, navigate, orgFields, p, returnUrl]
  );

  const handleRedirect = useCallback(() => {
    const signedIn = Boolean(auth.currentUser?.emailVerified);
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';

    if (returnUrl) {
      const parsed = parseReturnUrl(returnUrl, currentOrigin);
      const returnPath = parsed.path;
      const successState = {
        email: email ?? undefined,
        message: 'Email verified successfully! Your account is now fully set up.',
        showOnboarding: true,
      };

      if (isAuthenticatedPostVerificationRoute(returnPath) && signedIn) {
        if (parsed.isCrossOrigin) {
          window.location.assign(parsed.href);
        } else {
          navigate(returnPath, { state: successState });
        }
        return;
      }

      const loginMessage = 'Email verified successfully! Please sign in to continue.';
      if (orgFields) {
        const unsignedRedirect = resolveUnsignedPostVerificationRedirect({
          parsed,
          effectiveAcademySlug: getEffectiveAcademySlug(),
          effectiveOrgSlug: orgFields.slug,
          org: orgFields,
          academy,
          email,
          loginMessage,
          orgHostMode: hostMode,
        });
        if (unsignedRedirect) {
          applyPostVerificationRedirect(unsignedRedirect);
          return;
        }
      }

      const loginPath = orgFields
        ? buildOrgLoginPath(orgFields, returnPath, hostMode)
        : buildOrgLoginPath({ slug: orgSlug ?? '' }, returnPath);
      navigate(loginPath, {
        state: {
          email: email ?? undefined,
          message: loginMessage,
        },
      });
      return;
    }

    const dashboardPath = p?.('/dashboard') ?? (orgSlug ? `/club-academy/${orgSlug}/dashboard` : '/dashboard');
    navigate(dashboardPath, {
      state: {
        email: email ?? undefined,
        message: 'Email verified successfully! Your account is now fully set up.',
      },
    });
  }, [
    academy,
    applyPostVerificationRedirect,
    email,
    getEffectiveAcademySlug,
    hostMode,
    navigate,
    orgFields,
    orgSlug,
    p,
    returnUrl,
  ]);

  const handleEmailVerification = useCallback(
    async (verificationToken: string) => {
      setIsVerifying(true);
      setErrorMessage('');

      try {
        await applyActionCode(auth, verificationToken);
        if (auth.currentUser) {
          await reload(auth.currentUser);
          await auth.currentUser.getIdToken(true);
        }

        await useAuthStore.getState().refreshUser({ forceTokenRefresh: true });

        setVerificationStatus('success');
        toast.success('Email verified successfully!');

        const params = new URLSearchParams(window.location.search);
        params.delete('oobCode');
        params.delete('mode');
        params.delete('apiKey');
        params.delete('lang');
        const cleanSearch = params.toString();
        navigate(`${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`, { replace: true });

        setTimeout(() => {
          handleRedirect();
        }, 2000);
      } catch (error: unknown) {
        const code = (error as { code?: string })?.code;
        setVerificationStatus('error');
        const message =
          code === 'auth/invalid-action-code'
            ? 'The verification link is invalid or has expired.'
            : code === 'auth/expired-action-code'
              ? 'The verification link has expired. Please request a new one.'
              : 'Verification failed. Please try again.';
        setErrorMessage(message);
        toast.error(message);
        console.error('Email verification error:', error);
      } finally {
        setIsVerifying(false);
      }
    },
    [handleRedirect, location.pathname, navigate]
  );

  useEffect(() => {
    if (verificationAttemptedRef.current) {
      return;
    }

    if (oobCode && mode === 'verifyEmail') {
      verificationAttemptedRef.current = true;
      void handleEmailVerification(oobCode);
    } else if (token) {
      verificationAttemptedRef.current = true;
      void handleEmailVerification(token);
    }
  }, [oobCode, mode, token, handleEmailVerification]);

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Email address not found');
      return;
    }

    setIsResending(true);

    try {
      const verificationReturnUrl = buildResendVerificationReturnUrl(
        returnUrl,
        `${location.pathname}${location.search}`,
        window.location.origin
      );
      await AuthService.resendEmailVerification(email, { returnUrl: verificationReturnUrl });

      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to resend verification email';
      toast.error(message);
      console.error('Resend verification error:', error);
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigateToLogin('Please verify your email before logging in.');
  };

  const org = orgProfile?.organization;
  const loginPath = orgFields ? buildOrgLoginPath(orgFields, returnUrl ?? p?.('/dashboard') ?? undefined, hostMode) : '/login';

  if (loadingOrg) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-600 dark:text-gray-400">Loading…</p>
      </div>
    );
  }

  const mainHeading =
    verificationStatus === 'success'
      ? 'Email verified'
      : verificationStatus === 'error'
        ? 'Verification failed'
        : isVerifying
          ? 'Verifying…'
          : 'Check your email';

  const mainSubheading =
    verificationStatus === 'success'
      ? 'Redirecting you to continue'
      : verificationStatus === 'error'
        ? 'Try resending the verification email'
        : isVerifying
          ? 'Please wait while we confirm your address'
          : 'Open the link we sent to your inbox';

  return (
    <AcademyBrandingProvider primaryColor={org?.primaryColor ?? undefined} secondaryColor={org?.secondaryColor ?? undefined}>
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-tennis-50 px-4 py-12 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="mx-auto max-w-md">
          <div className="mb-8 text-center">
            {org?.logoUrl ? (
              <img
                src={org.logoUrl}
                alt={org.name}
                className="mx-auto mb-4 h-16 w-auto object-contain"
                referrerPolicy="no-referrer"
              />
            ) : org?.name ? (
              <p className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{org.name}</p>
            ) : null}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Verify your email</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{mainSubheading}</p>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">{mainHeading}</h2>

            {verificationEmailSendFailed && verificationStatus === 'pending' && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                <p className="text-sm text-amber-900 dark:text-amber-200">
                  Your account was created, but we could not send the verification email. Use{' '}
                  <strong>Resend Verification Email</strong> below or check that your email address is correct.
                </p>
              </div>
            )}

            {verificationStatus === 'success' && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-6 dark:border-green-800 dark:bg-green-900/20">
                <div className="flex flex-col items-center text-center">
                  <CheckCircle className="mb-4 h-12 w-12 text-green-600 dark:text-green-400" />
                  <p className="text-base font-medium text-green-900 dark:text-green-200">
                    Your email has been verified successfully!
                  </p>
                  <Loader2 className="mt-4 h-5 w-5 animate-spin text-primary-600" />
                </div>
              </div>
            )}

            {verificationStatus === 'error' && (
              <div className="space-y-4">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {errorMessage || 'The verification link is invalid or has expired.'}
                    </p>
                  </div>
                </div>
                <Button onClick={handleResendVerification} disabled={isResending} className="w-full btn-primary">
                  {isResending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>
                <Button variant="outline" onClick={handleGoToLogin} className="w-full">
                  Go to Login
                </Button>
              </div>
            )}

            {verificationStatus === 'pending' && !token && !oobCode && (
              <div className="space-y-4">
                <div className="rounded-lg border border-border bg-muted/30 p-4 dark:bg-muted/20">
                  <div className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" />
                    <div className="space-y-2 text-sm">
                      <p className="text-gray-900 dark:text-white">
                        We&apos;ve sent a verification link to{' '}
                        <span className="font-medium">{email || 'your email address'}</span>
                      </p>
                      <p className="text-gray-600 dark:text-gray-400">
                        Click the link in the email to verify your account. The link will expire in 24 hours.
                      </p>
                    </div>
                  </div>
                </div>
                {import.meta.env.DEV && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                    <p className="text-sm text-amber-900 dark:text-amber-200">
                      <strong>Development Mode:</strong> Check your terminal for the verification link when using Firebase emulators.
                    </p>
                  </div>
                )}
                <Button onClick={handleResendVerification} disabled={isResending} className="w-full btn-primary">
                  {isResending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>
                <Button variant="outline" onClick={handleGoToLogin} className="w-full">
                  Go to Login
                </Button>
              </div>
            )}

            {isVerifying && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="mb-4 h-12 w-12 animate-spin text-primary-600" />
                <p className="text-base font-medium text-gray-900 dark:text-white">Verifying your email…</p>
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Already verified?{' '}
            <Link to={loginPath} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </AcademyBrandingProvider>
  );
}
